import React, { useRef, useEffect, useCallback } from 'react';
import { sanitizeForFrame } from '../../utils/sanitize';

// Estilo base para conteúdo do CMS SEM <style> próprio (ex.: uma descrição simples) —
// deixa legível no tema escuro do app. O <style> do próprio conteúdo (ex.: o quiz, que
// pinta um card branco) vem DEPOIS e sobrescreve este base.
const BASE_STYLE = `<style>
  html, body { margin: 0; padding: 0; background: transparent; color: #e8e8ea;
    font-family: 'Roboto', system-ui, -apple-system, 'Segoe UI', sans-serif; line-height: 1.5;
    word-break: break-word; overflow: hidden; }
  a { color: #ff5a5f; }
  img, video, iframe { max-width: 100%; }
</style>`;

// Override aplicado DEPOIS do <style> do conteúdo (com !important, então vence) para o
// conteúdo (ex.: o quiz, que se pinta de branco) combinar com o tema ESCURO do app.
// Só mexe em cor/fundo/divisória e no accent dos rádios — as pílulas verde/vermelho das
// respostas (`.resposta`) ficam como estão (contrastam bem no escuro).
const DARK_OVERRIDE = `<style>
  html, body { background: transparent !important; color: #e8e8ea !important; }
  h1, h2, h3, h4, p, span, label, li { color: #e8e8ea !important; }
  h1, h2, h3 { color: #ffffff !important; }
  .opcao input[type="radio"] { accent-color: #E50914 !important; }
  .quiz-box { border-bottom-color: rgba(255,255,255,0.12) !important; }
</style>`;

// Desliga autoplay/preload dos vídeos EMBUTIDOS no conteúdo (ex.: um "documento" com
// vários tutoriais). Sem isto, os players do Bunny/Panda tocam TODOS juntos ao abrir a
// página — a pessoa deve clicar pra tocar o que quiser.
function neutralizeAutoplay(html) {
  let s = html || '';
  // remove 'autoplay' da permissão (allow) do iframe
  s = s.replace(/(allow="[^"]*?)\bautoplay\b\s*;?\s*/gi, '$1');
  // força autoplay=false&preload=none na URL dos embeds de vídeo
  s = s.replace(/(\bsrc=")([^"]*\/embed\/[^"]*?)(")/gi, (m, a, url, b) => {
    if (!/mediadelivery\.net|pandavideo\.com\.br|youtube|youtu\.be|vimeo/i.test(url)) return m;
    const sep = url.includes('?') ? '&' : '?';
    return a + url + sep + 'autoplay=false' + b;
  });
  return s;
}

/**
 * Renderiza o `additional_info` do conteúdo (HTML do CMS, que pode trazer o próprio
 * <style> — como o quiz em CSS puro) dentro de um <iframe sandbox>. Isso:
 *  - ISOLA o CSS do conteúdo (o `html,body{}` do quiz não vaza pra página);
 *  - PRESERVA o <style> (o quiz volta a funcionar: respostas escondidas até clicar);
 *  - PERMITE scripts/popups (o conteúdo do CMS é confiável e pode trazer vídeos
 *    embutidos — iframes do Bunny/YouTube — e links `target=_blank` que precisam rodar).
 * A altura é ajustada ao conteúdo (inclusive quando o quiz revela uma resposta).
 */
export default function AdditionalInfoFrame({ html }) {
  const ref = useRef(null);

  const fit = useCallback(() => {
    const ifr = ref.current;
    const doc = ifr && ifr.contentDocument;
    if (!doc) return;
    const h = doc.documentElement.scrollHeight;
    if (h) ifr.style.height = `${h}px`;
  }, []);

  const onLoad = useCallback(() => {
    const ifr = ref.current;
    const doc = ifr && ifr.contentDocument;
    if (!doc) return;
    fit();
    // re-mede em layout tardio (fontes/imagens) e quando o quiz revela uma resposta
    [80, 300, 800].forEach((t) => setTimeout(fit, t));
    doc.addEventListener('change', () => setTimeout(fit, 30));
    doc.addEventListener('click', () => setTimeout(fit, 30));
  }, [fit]);

  useEffect(() => {
    const onResize = () => fit();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [fit]);

  return (
    <iframe
      ref={ref}
      title="Conteúdo complementar"
      sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-presentation"
      scrolling="no"
      onLoad={onLoad}
      srcDoc={BASE_STYLE + sanitizeForFrame(neutralizeAutoplay(html)) + DARK_OVERRIDE}
      style={{ width: '100%', border: 'none', display: 'block', background: 'transparent' }}
    />
  );
}
